import { useMemo } from 'react'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreVertical, Copy, Trash2, Power, Link2, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { parseChainYaml } from '@/lib/chainYaml'
import { useRoles } from '@/hooks'
import { CHAIN_CATEGORY_DOT_COLORS, type ChainDefinitionSummary } from '@/types'

interface ChainCardProps {
  chain: ChainDefinitionSummary
  canStart: boolean
  canDuplicate: boolean
  canToggle: boolean
  canDelete: boolean
  onNavigate: (chain: ChainDefinitionSummary) => void
  onStart: (chain: ChainDefinitionSummary) => void
  onDuplicate: (chain: ChainDefinitionSummary) => void
  onToggleActive: (chain: ChainDefinitionSummary) => void
  onDelete: (chain: ChainDefinitionSummary) => void
}

export function ChainCard({
  chain,
  canStart,
  canDuplicate,
  canToggle,
  canDelete,
  onNavigate,
  onStart,
  onDuplicate,
  onToggleActive,
  onDelete,
}: ChainCardProps) {
  const { data: roles } = useRoles()

  const linkCount = useMemo(() => {
    try {
      return parseChainYaml(chain.yamlContent).links.length
    } catch {
      return 0
    }
  }, [chain.yamlContent])

  const roleLabel = useMemo(() => {
    if (!roles || chain.permittedRoleIds.length === 0) return null
    const nameById = new Map(roles.map((r) => [r.id, r.name]))
    const names = chain.permittedRoleIds.map((id) => nameById.get(id)).filter((n): n is string => !!n)
    if (names.length === 0) return null
    if (names.length <= 2) return names.join(', ')
    return `${names[0]}, ${names[1]} +${names.length - 2}`
  }, [roles, chain.permittedRoleIds])

  const dotColor = CHAIN_CATEGORY_DOT_COLORS[chain.category ?? 'Uncategorized']

  return (
    <Card
      className="group flex cursor-pointer flex-col transition-colors hover:bg-muted has-[[data-state=open]]:bg-muted relative rounded-2xl bg-card p-4 gap-6 border-border"
      data-testid={`chain-card-${chain.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onNavigate(chain)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onNavigate(chain)
        }
      }}
    >
      <div className="absolute top-4 right-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="shrink-0 text-foreground focus:outline-none p-2 rounded-lg hover:bg-[rgba(224,229,229,0.5)] data-[state=open]:bg-[rgba(224,229,229,0.5)]"
              aria-label={`More options for ${chain.displayName}`}
              data-testid={`chain-menu-trigger-${chain.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="bottom"
            align="end"
            sideOffset={4}
            onClick={(e) => e.stopPropagation()}
            className="border-none shadow-elevated rounded-lg py-2 px-0 bg-background gap-0.5 min-w-0"
          >
            {canStart && chain.isActive && (
              <DropdownMenuItem
                onClick={() => onStart(chain)}
                className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                data-testid={`chain-start-${chain.id}`}
              >
                <Play className="h-5 w-5" />
                Start
              </DropdownMenuItem>
            )}
            {canStart && chain.isActive && canDuplicate && <DropdownMenuSeparator />}
            {canDuplicate && (
              <DropdownMenuItem
                onClick={() => onDuplicate(chain)}
                className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
              >
                <Copy className="h-5 w-5" />
                Duplicate
              </DropdownMenuItem>
            )}
            {canDelete && !chain.isSystem && !chain.isActive && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(chain)}
                  className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                >
                  <Trash2 className="h-5 w-5" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
            {canToggle && !chain.isSystem && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onToggleActive(chain)}
                  className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                >
                  <Power className="h-5 w-5" />
                  {chain.isActive ? 'Deactivate' : 'Activate'}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CardHeader className="flex-1 p-0 pr-16">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-accent-soft flex items-center justify-center text-background shrink-0">
              <Link2 className="h-4 w-4" />
            </div>
            <CardTitle className="text-sm font-bold leading-[1.6] tracking-[0.015em] text-card-foreground break-all">{chain.displayName}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs" style={{ borderColor: 'rgba(118,124,150,1)' }}>
              V{chain.version}
            </Badge>
            <Badge
              variant="outline"
              className={cn(
                'text-xs font-bold leading-[1.6] tracking-[0.015em] rounded-full px-2.5',
                chain.isActive
                  ? 'h-6 bg-active-badge-bg text-active-badge-fg hover:bg-active-badge-bg'
                  : 'h-6 bg-inactive-badge-bg text-inactive-badge-fg hover:bg-inactive-badge-bg',
              )}
              style={{ borderColor: chain.isActive ? 'rgba(38,132,255,1)' : 'rgba(128,133,155,1)' }}
            >
              {chain.isActive ? 'Active' : 'Inactive'}
            </Badge>
            <Badge variant="outline" className="text-xs" style={{ borderColor: 'rgba(118,124,150,1)' }}>
              {chain.isSystem ? 'System' : 'Custom'}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <div>
        {chain.description && (
          <p className="text-sm font-normal leading-[1.4] tracking-[0.015em] text-card-foreground/72 line-clamp-2">
            {chain.description}
          </p>
        )}
      </div>

      <div className="flex items-center gap-5 text-[12px] font-normal leading-[1.6] tracking-[0.015em] text-card-foreground">
        <span className="flex items-center gap-1.5">
          <span className={cn('inline-block h-2 w-2 rounded-full', dotColor)} />
          {chain.category ?? 'Uncategorized'}
        </span>
        <span className="flex items-center gap-1.5">
          <Link2 className="h-4 w-4" />
          Links: {linkCount}
        </span>
        {roleLabel && (
          <span data-testid={`chain-card-roles-${chain.id}`}>
            Role: {roleLabel}
          </span>
        )}
      </div>
    </Card>
  )
}
